import React from 'react';
import { Star } from 'lucide-react';
import TrendingReasonChip from '../TrendingReasonChip';

const TMDB_LOGO_BASE = 'https://image.tmdb.org/t/p/w45';

const ProviderRow = ({ providers, max = 5 }) => {
  if (!providers || providers.length === 0) return null;
  return (
    <div className="provider-row" data-testid="provider-row">
      {providers.slice(0, max).map((p) => (
        <span key={p.provider_id} className="provider-chip" title={p.name}>
          {p.logo_path ? (
            <img
              src={`${TMDB_LOGO_BASE}${p.logo_path}`}
              alt={p.name}
              loading="lazy"
              width="20"
              height="20"
            />
          ) : (
            <span className="provider-name">{p.name}</span>
          )}
        </span>
      ))}
      <style jsx>{`
        .provider-row {
          display: flex;
          gap: 0.4rem;
          flex-wrap: wrap;
          margin-top: 0.65rem;
        }
        .provider-chip {
          display: inline-flex;
          align-items: center;
          padding: 3px;
          background: #0f0f0f;
          border: 1px solid #232323;
          border-radius: 6px;
        }
        .provider-chip img {
          display: block;
          border-radius: 3px;
        }
        .provider-name {
          color: #d4d4d4;
          font-size: 0.7rem;
          padding: 0 0.35rem;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

const RankBadge = ({ rank }) => {
  const isTop = rank === 1;
  return (
    <span className={`rank-badge ${isTop ? 'rank-gold' : ''}`} data-testid={`rank-badge-${rank}`}>
      #{rank}
      <style jsx>{`
        .rank-badge {
          position: absolute;
          top: 0.75rem;
          left: 0.75rem;
          background: #0a0a0a;
          color: #fff;
          font-weight: 800;
          font-size: 0.75rem;
          padding: 0.3rem 0.55rem;
          border-radius: 6px;
          letter-spacing: 0.04em;
          border: 1px solid #2a2a2a;
          z-index: 2;
        }
        .rank-gold {
          background: linear-gradient(120deg, #E62F88 0%, #f5b942 100%);
          color: #0a0a0a;
          border: none;
        }
      `}</style>
    </span>
  );
};

/* ---------- LEADERBOARD CARD (#1, large bento) ---------- */
export const ShowLeaderboardCard = ({ show, rank, onOpen }) => (
  <article
    className="leaderboard-card"
    data-testid={`leaderboard-card-${show.tmdb_id}`}
    onClick={() => onOpen(show)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen(show);
      }
    }}
    role="button"
    tabIndex={0}
  >
    <div className="poster-wrap">
      <RankBadge rank={rank} />
      {show.image_url ? (
        <img src={show.image_url} alt={`${show.title} poster`} loading="lazy" />
      ) : (
        <div className="poster-fallback">No poster</div>
      )}
    </div>
    <div className="content">
      <span className="eyebrow">Top of the flock</span>
      <h2 className="title">{show.title}</h2>
      <div className="meta">
        {show.year && <span>{show.year}</span>}
        {show.year && show.vote_average ? <span className="dot">•</span> : null}
        {show.vote_average ? (
          <span className="score">
            <Star size={14} className="star" /> {show.vote_average.toFixed(1)}
          </span>
        ) : null}
      </div>
      {show.trending_reason && (
        <div className="reason-row">
          <TrendingReasonChip reason={show.trending_reason} size="lg" />
        </div>
      )}
      <p className="lead-overview">{show.overview || 'No overview available yet — but the flock is talking.'}</p>
      <ProviderRow providers={show.providers} max={6} />
      <span className="open-hint">View details →</span>
    </div>

    <style jsx>{`
      .leaderboard-card {
        display: grid;
        grid-template-columns: 200px 1fr;
        gap: 1.25rem;
        background: linear-gradient(135deg, #161616 0%, #0e0e0e 100%);
        border: 1px solid #262626;
        border-radius: 18px;
        padding: 1.1rem;
        transition: border-color 0.25s, transform 0.25s, box-shadow 0.25s;
        position: relative;
        overflow: hidden;
        cursor: pointer;
        align-items: stretch;
      }
      .leaderboard-card::before {
        content: '';
        position: absolute;
        inset: -1px;
        border-radius: 18px;
        padding: 1px;
        background: linear-gradient(135deg, rgba(230, 47, 136, 0.45), transparent 60%);
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
                mask-composite: exclude;
        pointer-events: none;
      }
      .leaderboard-card:hover {
        transform: translateY(-2px);
        border-color: rgba(230, 47, 136, 0.45);
        box-shadow: 0 24px 60px -30px rgba(230, 47, 136, 0.4);
      }
      .leaderboard-card:focus-visible {
        outline: 2px solid #E62F88;
        outline-offset: 2px;
      }
      .poster-wrap {
        position: relative;
        border-radius: 12px;
        overflow: hidden;
        aspect-ratio: 2 / 3;
        background: #0a0a0a;
      }
      .poster-wrap img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      .poster-fallback {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #555;
        font-size: 0.85rem;
      }
      .content {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }
      .eyebrow {
        color: #E62F88;
        font-size: 0.7rem;
        letter-spacing: 0.2em;
        font-weight: 800;
        text-transform: uppercase;
        margin-bottom: 0.5rem;
      }
      .title {
        color: #fff;
        font-size: clamp(1.4rem, 2.6vw, 1.85rem);
        font-weight: 800;
        letter-spacing: -0.01em;
        line-height: 1.1;
        margin: 0 0 0.5rem;
      }
      .meta {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #a1a1aa;
        font-size: 0.85rem;
        margin-bottom: 0.5rem;
      }
      .reason-row {
        margin-bottom: 0.75rem;
      }
      .dot {
        color: #444;
      }
      .score {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        color: #facc15;
        font-weight: 700;
      }
      .lead-overview {
        color: #c4c4cc;
        font-size: 0.92rem;
        line-height: 1.55;
        margin: 0 0 0.5rem;
      }
      .open-hint {
        margin-top: auto;
        color: #E62F88;
        font-size: 0.78rem;
        font-weight: 700;
        opacity: 0;
        transition: opacity 0.18s;
      }
      .leaderboard-card:hover .open-hint,
      .leaderboard-card:focus-visible .open-hint {
        opacity: 1;
      }
      @media (max-width: 720px) {
        .leaderboard-card {
          grid-template-columns: 110px 1fr;
          gap: 0.85rem;
          padding: 0.9rem;
        }
        .overview { -webkit-line-clamp: 3; }
      }
    `}</style>
  </article>
);

/* ---------- VERTICAL CARD (#2 & #3 — used in 'Also grazing' top trio) ---------- */
export const ShowVerticalCard = ({ show, rank, onOpen }) => (
  <article
    className="vertical-card"
    data-testid={`vertical-card-${show.tmdb_id}`}
    onClick={() => onOpen(show)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen(show);
      }
    }}
    role="button"
    tabIndex={0}
  >
    <div className="poster-wrap">
      <RankBadge rank={rank} />
      {show.image_url ? (
        <img src={show.image_url} alt={`${show.title} poster`} loading="lazy" />
      ) : (
        <div className="poster-fallback">No poster</div>
      )}
    </div>
    <div className="content">
      <h2 className="title">{show.title}</h2>
      <div className="meta">
        {show.year && <span>{show.year}</span>}
        {show.year && show.vote_average ? <span className="dot">•</span> : null}
        {show.vote_average ? (
          <span className="score">
            <Star size={13} className="star" /> {show.vote_average.toFixed(1)}
          </span>
        ) : null}
      </div>
      <p className="vert-overview">{show.overview}</p>
      <ProviderRow providers={show.providers} max={4} />
    </div>
    <style jsx>{`
      .vertical-card {
        background: #141414;
        border: 1px solid #242424;
        border-radius: 14px;
        padding: 0.9rem;
        display: flex;
        flex-direction: column;
        transition: border-color 0.18s, transform 0.18s;
        cursor: pointer;
      }
      .vertical-card:hover {
        border-color: rgba(230, 47, 136, 0.45);
        transform: translateY(-2px);
      }
      .vertical-card:focus-visible {
        outline: 2px solid #E62F88;
        outline-offset: 2px;
      }
      .poster-wrap {
        position: relative;
        aspect-ratio: 2 / 3;
        border-radius: 10px;
        overflow: hidden;
        background: #0a0a0a;
        margin-bottom: 0.85rem;
      }
      .poster-wrap img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      .poster-fallback {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #555;
        font-size: 0.8rem;
      }
      .content {
        display: flex;
        flex-direction: column;
      }
      .title {
        color: #fff;
        font-size: 1.05rem;
        font-weight: 700;
        line-height: 1.2;
        margin: 0 0 0.4rem;
      }
      .meta {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        color: #a1a1aa;
        font-size: 0.78rem;
        margin-bottom: 0.6rem;
      }
      .dot {
        color: #444;
      }
      .score {
        display: inline-flex;
        align-items: center;
        gap: 0.2rem;
        color: #facc15;
        font-weight: 700;
      }
      .vert-overview {
        color: #b4b4b8;
        font-size: 0.85rem;
        line-height: 1.5;
        margin: 0;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    `}</style>
  </article>
);
